// Potwierdzenia (D-4, D-5): zastępuje natywne window.confirm. Promise-based useConfirm().
// Tytuł pytaniem z konkretem, jedno zdanie konsekwencji, przycisk niszczący (Danger)
// lub primary, obok Secondary "Anuluj". Fokus startowy na "Anuluj".
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Modal } from "./Modal";

type ConfirmOptions = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type ConfirmState = ConfirmOptions & { open: boolean };

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState>({ open: false, title: "" });
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    setState({ ...opts, open: true });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    setState((s) => ({ ...s, open: false }));
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={state.open}
        onClose={() => close(false)}
        labelledBy="confirm-dialog-title"
        panelClassName="modal__panel confirm-modal__panel"
        initialFocusSelector="[data-confirm-cancel]"
      >
        <h2 id="confirm-dialog-title" className="modal__title">
          {state.title}
        </h2>
        {state.body ? <p className="modal__body">{state.body}</p> : <div style={{ height: 12 }} />}
        <div className="modal__actions">
          <button
            type="button"
            className="btn-secondary"
            data-confirm-cancel
            onClick={() => close(false)}
          >
            {state.cancelLabel ?? "Anuluj"}
          </button>
          <button
            type="button"
            className={state.danger ? "btn-danger" : "btn"}
            onClick={() => close(true)}
          >
            {state.confirmLabel ?? "Potwierdź"}
          </button>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
